<template>
  <form @submit.prevent="submit()">
    <component
      v-for="input in inputs"
      :key="input.key"
      :is="input.component"
      :config="input"
      v-model="form[input.key]">
    </component>

    <v-card
      v-if="form.picture"
      :img="form.picture"
      height="200px"
      style="width:200px">
    </v-card>

    <component
      v-for="input in advancedInputs"
      :key="input.key"
      :is="input.component"
      :config="input"
      v-model="form[input.key]">
    </component>

    <offer-preview :value="offerData"></offer-preview>

    <v-card-row actions>
      <v-btn
        v-if="service.id"
        error light flat
        class="mr-3"
        @click.native.stop="deleteService()">
        {{ $t('service.form.delete') }}
      </v-btn>
      <v-btn
        type="submit"
        primary light ripple
        :loading="loading">
        {{ $t('service.form.save') }}
      </v-btn>
    </v-card-row>
  </form>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import Service from '@/api/service'
  import OfferPreview from '@/components/messenger/previews/Offer.vue'
  import {
    TextInput,
    PriceInput,
    FileInput,
    DateInput,
    AvailabilityTypeInput,
    ServiceTypeInput,
    MinuteInput,
    OfferTypeInput,
    DelayInput,
    ServicesInput,
    ExpertsInput,
    RichTextInput,
    PortfolioInput
  } from '@/components/inputs'
  export default {
    components: {
      OfferPreview
    },
    props: {
      service: {
        type: Object,
        required: true
      }
    },
    data () {
      const serviceOptions = this.service.service_options || {}
      return {
        error: {},
        form: {
          serviceType: this.service.service_type,
          title: this.service.title,
          price: this.service.price,
          'service--serviceType': serviceOptions.service_type,
          'service--responseDelay': serviceOptions.response_delay,
          'prepaidMinute--minutes': serviceOptions.minutes,
          'mail--responseDelay': serviceOptions.response_delay,
          'mail--wordCount': serviceOptions.word_count,
          'product--message': serviceOptions.message,
          'availability--duration': serviceOptions.duration,
          'availability--availabilityType': serviceOptions.availability_type,
          experts: this.service.admin_ids || [],
          description: (this.service.description || {}).full || '',
          subtitle: this.service.subtitle,
          picture: (this.service.picture || {}).normal,
          shortDescription: (this.service.description || {}).short,
          keywords: (this.service.keywords || []).join(', '),
          expire: this.service.expire_at,
          portfolio: this.service.portfolio || [],
          maxUserPurchase: this.service.max_user_purchase,
          offerType: this.service.promotion
            ? 'promotion'
            : this.service.is_public !== false
              ? 'public'
              : 'private'
        }
      }
    },
    computed: {
      ...mapGetters({
        loading: 'skiliko/services/isLoading'
      }),
      isOffer () {
        return !this.service.id || Service.isOfferType(this.service.service_type)
      },
      typeServiceInputs () {
        return this.service.id ? [] : [
          { key: 'serviceType', required: true, component: ServiceTypeInput }
        ]
      },
      servicesInputs () {
        // TODO: this validation triggers errors in console
        // [Vue warn]: <transition-group> children must be keyed: <div>
        const prepaidMinuteValidations = [
          minutes => {
            if (this.form.price / process.env.MIN_PRICE_PER_MINUTE < minutes) {
              return this.$t('service.form.errors.minutes', { price: process.env.MIN_PRICE_PER_MINUTE })
            }
          }
        ]

        return {
          service: [
            { key: 'service--serviceType', required: true, component: ServicesInput },
            { key: 'service--responseDelay', component: DelayInput }
          ],
          prepaid_minute: [
            { key: 'prepaidMinute--minutes', required: true, component: MinuteInput, errors: prepaidMinuteValidations }
          ],
          product: [
            { key: 'product--message', required: true, component: RichTextInput }
          ],
          mail: [
            { key: 'mail--responseDelay', component: DelayInput },
            { key: 'mail--wordCount', component: TextInput, type: 'number' }
          ],
          availability: [
            { key: 'availability--availabilityType', required: true, component: AvailabilityTypeInput },
            { key: 'availability--duration', required: true, component: MinuteInput }
          ]
        }[this.form.serviceType] || []
      },
      advancedInputs () {
        return [
          { key: 'picture', component: FileInput },
          { key: 'subtitle', component: TextInput },
          { key: 'shortDescription', component: TextInput, multiLine: true, counter: true, max: 150 },
          { key: 'keywords', component: TextInput },
          (this.isOffer ? { key: 'expire', component: DateInput } : null),
          (this.isOffer ? { key: 'maxUserPurchase', component: TextInput, type: 'number', disabled: this.service.id } : null)
        ]
          .filter(e => e)
          .map(input => ({
            ...input,
            label: this.$t(`service.form.labels.${input.key}`),
            errors: (this.error[input.key] || []).map(e => () => e)
          }))
      },
      offerInputs () {
        return [
          ...this.typeServiceInputs,
          { key: 'title', required: true, component: TextInput },
          { key: 'price', required: true, component: PriceInput },
          ...this.servicesInputs,
          { key: 'offerType', required: true, component: OfferTypeInput },
          { key: 'description', required: true, component: RichTextInput },
          { key: 'portfolio', component: PortfolioInput },
          (this.$can('assign', 'services') ? { key: 'experts', component: ExpertsInput } : null)
        ]
      },
      pageInputs () {
        return [
          { key: 'title', required: true, component: TextInput },
          { key: 'description', required: true, component: RichTextInput, autoGrow: true, multiLine: true },
          { key: 'portfolio', component: PortfolioInput }
        ]
      },
      inputs () {
        return (this.isOffer ? this.offerInputs : this.pageInputs)
          .filter(e => e)
          .map(input => ({
            ...input,
            label: this.$t(`service.form.labels.${input.key}`),
            errors: [...(input.errors || []), ...(this.error[input.key] || []).map(e => () => e)]
          }))
      },
      offerData () {
        const serviceOptions = {
          prepaid_minute: {
            minutes: this.form['prepaidMinute--minutes']
          },
          product: {
            message: this.form['product--message']
          },
          mail: {
            response_delay: this.form['mail--responseDelay'],
            word_count: this.form['mail--wordCount']
          },
          service: {
            service_type: this.form['service--serviceType'],
            response_delay: this.form['service--responseDelay']
          },
          availability: {
            duration: this.form['availability--duration'],
            availability_type: this.form['availability--availabilityType']
          }
        }[this.form.serviceType] || this.service.service_options

        return {
          title: this.form.title,
          subtitle: this.form.subtitle,
          keywords: this.form.keywords,
          description: {
            full: this.form.description,
            short: this.form.shortDescription
          },
          picture: {
            normal: this.form.picture
          },
          admin_ids: this.form.experts,
          price: this.form.price,
          service_type: this.form.serviceType,
          service_options: serviceOptions,
          expire_at: this.form.expire,
          promotion: this.form.offerType === 'promotion',
          is_public: this.form.offerType !== 'private',
          portfolio: this.form.portfolio,
          max_user_purchase: this.form.maxUserPurchase
        }
      }
    },
    methods: {
      ...mapActions({
        update: 'skiliko/services/update',
        create: 'skiliko/services/create',
        remove: 'skiliko/services/remove'
      }),
      submit () {
        if (this.service.id) {
          this.update({ id: this.service.id, data: this.offerData })
        } else {
          this.create({ data: this.offerData })
            .then(service => {
              this.$router.push({ name: 'Service', params: { id: service.id } })
            })
        }
      },
      deleteService () {
        if (confirm(this.$t('service.form.confirmDeletion'))) {
          this.remove({ id: this.service.id })
            .then(_ => this.$router.push({ path: '/' }))
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/service/Form.vue